import React from "react";
import { Activity, ChevronRight, Eye, GitBranch, Play, Plus } from "lucide-react";
import { Pill } from "../common/Pill";
import { cvar, fmtDate, fmtDateTime } from "../../utils";
import type { FlatVersion } from "../../api/workbench";
import type { CorpusSet, EvalSet, Workspace, WorkstreamBundle } from "../../types";

export interface SimDirectoryProps {
  workspaces: Workspace[];
  onNew: () => void;
  onOpenActive: (w: Workspace) => void;
  onOpenCompleted: (w: Workspace) => void;
  corpuses: CorpusSet[];
  /** Saved bundle versions (agent-bundles API) — names the pinned version per workspace. */
  bundleVersions: FlatVersion[];
  evalSets: EvalSet[];
  wsBundles: WorkstreamBundle[];
  wsName: (id: string) => string;
  onLaunchTour?: () => void;
}

/* Simulations directory (the landing page) — lists active workspaces and the
   historical roll of completed runs. Click a row to open it. */
export function SimDirectory({
  workspaces, onNew, onOpenActive, onOpenCompleted, corpuses, bundleVersions,
  evalSets, wsBundles, onLaunchTour,
}: SimDirectoryProps): JSX.Element {
  const active = workspaces.filter((w) => w.status !== "completed");
  const completed = workspaces.filter((w) => w.status === "completed");
  const cfgSummary = (w: Workspace): string => {
    const cfg = w.config;
    if (!cfg) return "—";
    const c = corpuses.find((x) => x.id === cfg.corpusId);
    const pin = bundleVersions.find(
      (v) => v.bundleId === cfg.checkBundleId && v.versionId === cfg.checkVerId,
    );
    const ev = evalSets.find((e) => e.id === cfg.evalId);
    const wb = (wsBundles || []).find((b) => b.id === cfg.wsBundleId);
    const parts = [
      c && c.name,
      wb && wb.name,
      cfg.matters.length + " matter" + (cfg.matters.length === 1 ? "" : "s"),
      pin ? pin.bundleName + " · " + pin.label : "version " + cfg.checkVerId,
      ev ? ev.name : "no eval",
    ].filter(Boolean);
    return parts.join(" · ");
  };
  const onRowKey = (e: React.KeyboardEvent, fn: () => void): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fn();
    }
  };
  return (
    <main className="page">
      <div className="content scroll">
        <div className="pagehd" style={{ padding: "24px 0 16px", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="h1">Simulations</h1>
            <div className="psub" style={{ marginTop: 6, maxWidth: 680, lineHeight: 1.55 }}>
              Production minus the live model, plus a ground truth — for financings and fund
              formations. Calibrate each check, run a real corpus end-to-end, and review every AI
              decision before the system touches a live inbox.
            </div>
          </div>
          <div className="actions">
            {onLaunchTour && (
              <button className="tourbtn" onClick={onLaunchTour} aria-label="Launch product tour">
                <Eye size={13} /> Tour
              </button>
            )}
            <button className="btn btn-primary" onClick={onNew}>
              <Plus size={15} /> New simulation
            </button>
          </div>
        </div>

        <h2 className="up" style={{ margin: "6px 0 8px" }}>
          Active · {active.length}
        </h2>
        {active.length === 0 && (
          <div className="card" style={{ padding: 22, textAlign: "center" }}>
            <div className="empty" style={{ padding: 0 }}>
              No active workspaces. Start one with <b>New simulation</b>.
            </div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
          {active.map((w) => (
            <div
              className="card asset"
              key={w.id}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => onRowKey(e, () => onOpenActive(w))}
              style={{
                padding: 14,
                display: "flex",
                alignItems: "center",
                gap: 14,
                cursor: "pointer",
              }}
              onClick={() => onOpenActive(w)}
            >
              <div className="logo" style={{ background: "var(--subtle)", color: "var(--fg)" }}>
                <Activity size={14} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{w.name}</div>
                <div className="mono tert" style={{ fontSize: 11, marginTop: 2 }}>
                  created {fmtDate(w.createdAt)} · {cfgSummary(w)}
                </div>
              </div>
              <Pill color="--blue" label="ready" />
              <span className="btn btn-ghost btn-sm" aria-hidden="true">
                <Play size={13} /> Open
              </span>
            </div>
          ))}
        </div>

        <h2 className="up" style={{ margin: "6px 0 8px" }}>
          Completed · {completed.length}
        </h2>
        {completed.length === 0 && (
          <div className="card" style={{ padding: 22, textAlign: "center" }}>
            <div className="empty" style={{ padding: 0 }}>
              No completed runs yet.
            </div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {completed.map((w) => {
            const run = w.run;
            return (
              <div
                className="card asset"
                key={w.id}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => onRowKey(e, () => onOpenCompleted(w))}
                style={{
                  padding: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  cursor: "pointer",
                }}
                onClick={() => onOpenCompleted(w)}
              >
                <div className="logo" style={{ background: "var(--subtle)", color: "var(--fg)" }}>
                  <GitBranch size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{w.name}</div>
                  <div className="mono tert" style={{ fontSize: 11, marginTop: 2 }}>
                    created {fmtDate(w.createdAt)} · ran{" "}
                    {fmtDateTime(w.completedAt || (run ? run.when : undefined))} ·{" "}
                    {run && run.config ? run.config.emails : 0} emails ·{" "}
                    {run && run.config ? run.config.checkVersion : ""}
                  </div>
                </div>
                {run && run.eval && (
                  <div style={{ textAlign: "right" }}>
                    <div
                      className="mono"
                      style={{
                        fontSize: 15,
                        fontWeight: 500,
                        color: cvar(run.eval.catPct >= 80 ? "--green" : "--amber"),
                      }}
                    >
                      {run.eval.catPct}%
                    </div>
                    <div className="tert" style={{ fontSize: 10 }}>
                      catalog
                    </div>
                  </div>
                )}
                <ChevronRight size={18} color="var(--tert)" />
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
